import {
  deleteApprovalReviewer,
  listPortfolioApprovals,
  updateApprovalReviewer,
  type CoworkPortfolioApproval,
} from "@/cowork/api"
import { useCollectionScope } from "@/cowork/scope-url"
import { useLanguage } from "@/context/language"
import { ApprovalDetailDialog } from "@/components/approval-detail-dialog"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import { createMemo, createResource, For, onCleanup, Show, type JSX } from "solid-js"
import { createStore } from "solid-js/store"
import { Portal } from "solid-js/web"

export function CoworkApprovalsPage() {
  const language = useLanguage()
  const scope = useCollectionScope()
  const dialog = useDialog()
  const [state, setState] = createStore({
    mounted: true,
    search: "",
    status: "all",
    editing: undefined as CoworkPortfolioApproval | undefined,
    reviewerEmail: "",
    deleting: undefined as CoworkPortfolioApproval | undefined,
    saving: "",
    error: "",
  })
  onCleanup(() => setState("mounted", false))
  const scopeKey = () => `${scope().companyID ?? ""}:${scope().projectID ?? ""}:${scope().periodID ?? ""}`
  const [approvals, approvalActions] = createResource(
    scopeKey,
    () =>
      listPortfolioApprovals({
        entityID: scope().companyID || "ALL",
        projectID: scope().projectID,
        periodID: scope().periodID,
      }),
  )
  const openDetails = (approval: CoworkPortfolioApproval) => {
    const scope = scopeKey()
    let open = true
    void dialog.show(() => <ApprovalDetailDialog
      approval={approval}
      active={() => state.mounted && open && scopeKey() === scope}
      onUpdated={(value) => approvalActions.mutate((items) => items?.map((item) => item.id === value.approval_id
        ? { ...item, comments: value.comments, status: value.status, updated_at: value.updated_at }
        : item))}
    />, () => { open = false })
  }
  const filtered = createMemo(() => {
    const query = state.search.trim().toLowerCase()
    return (approvals() ?? []).filter((approval) => {
      if (state.status !== "all" && approval.status !== state.status) return false
      if (!query) return true
      return `${approval.report_title} ${approval.approver_email} ${approval.company_name} ${approval.project_name} ${approval.period_name ?? ""}`
        .toLowerCase()
        .includes(query)
    })
  })
  const counts = createMemo(() => {
    const records = approvals() ?? []
    return {
      total: records.length,
      requested: records.filter((item) => approvalKind(item.status) === "requested").length,
      approved: records.filter((item) => approvalKind(item.status) === "approved").length,
      rejected: records.filter((item) => approvalKind(item.status) === "rejected").length,
    }
  })

  const saveReviewer = async (event: SubmitEvent) => {
    event.preventDefault()
    const approval = state.editing
    const email = state.reviewerEmail.trim()
    if (!approval || !email || state.saving) return
    setState({ saving: approval.id, error: "" })
    try {
      await updateApprovalReviewer(
        approval.project_id,
        approval.id,
        email,
        approval.company_id,
      )
      await approvalActions.refetch()
      setState({ editing: undefined, reviewerEmail: "" })
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("saving", "")
    }
  }

  const removeApproval = async () => {
    const approval = state.deleting
    if (!approval || state.saving) return
    setState({ saving: approval.id, error: "" })
    try {
      await deleteApprovalReviewer(approval.project_id, approval.id, approval.company_id)
      await approvalActions.refetch()
      setState("deleting", undefined)
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("saving", "")
    }
  }

  return (
    <div class="min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10">
      <div class="mx-auto max-w-[1400px]">
        <header>
          <h1 class="text-[30px] font-semibold tracking-[-0.025em]">{language.t("cowork.usage.approvals")}</h1>
          <p class="mt-1 max-w-3xl text-[13px] text-v2-text-text-muted">
            {language.t("cowork.enterprise.approvals.description")}
          </p>
        </header>

        <div class="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric label={language.t("cowork.enterprise.approvals.total")} value={counts().total} />
          <Metric label={language.t("cowork.enterprise.approvals.pending")} value={counts().requested} />
          <Metric label={language.t("cowork.enterprise.approvals.approved")} value={counts().approved} />
          <Metric label={language.t("cowork.enterprise.approvals.rejected")} value={counts().rejected} />
        </div>

        <div class="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <input
            class="h-10 w-full rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none sm:max-w-sm"
            value={state.search}
            placeholder={language.t("cowork.enterprise.approvals.search")}
            aria-label={language.t("cowork.enterprise.approvals.search")}
            onInput={(event) => setState("search", event.currentTarget.value)}
          />
          <select
            class="h-10 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px]"
            value={state.status}
            aria-label={language.t("cowork.enterprise.approvals.statusFilter")}
            onChange={(event) => setState("status", event.currentTarget.value)}
          >
            <option value="all">{language.t("cowork.enterprise.approvals.allStatuses")}</option>
            <option value="requested">{language.t("cowork.enterprise.approvals.pending")}</option>
            <option value="approved">{language.t("cowork.enterprise.approvals.approved")}</option>
            <option value="rejected">{language.t("cowork.enterprise.approvals.rejected")}</option>
          </select>
        </div>

        <Show when={state.error}>
          <div class="mt-3 rounded-lg bg-v2-state-bg-danger px-4 py-3 text-[11px] text-v2-state-fg-danger">{state.error}</div>
        </Show>

        <section class="mt-3 overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base">
          <Show
            when={!approvals.loading && filtered().length > 0}
            fallback={
              <div class="grid min-h-64 place-items-center p-8 text-center text-[12px] text-v2-text-text-muted">
                {approvals.loading
                  ? language.t("common.loading")
                  : language.t("cowork.enterprise.approvals.empty")}
              </div>
            }
          >
            <div class="overflow-x-auto">
              <table class="w-full min-w-[960px] border-collapse text-left">
                <thead>
                  <tr class="border-b border-v2-border-border-base text-[10px] font-semibold uppercase tracking-[0.12em] text-v2-text-text-faint">
                    <th class="px-5 py-3">{language.t("cowork.enterprise.approvals.report")}</th>
                    <th class="px-4 py-3">{language.t("cowork.enterprise.approvals.reviewer")}</th>
                    <th class="px-4 py-3">{language.t("cowork.usage.status")}</th>
                    <th class="px-4 py-3">{language.t("cowork.enterprise.approvals.requestedAt")}</th>
                    <th class="px-4 py-3">{language.t("cowork.enterprise.approvals.updatedAt")}</th>
                    <th class="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  <For each={filtered()}>
                    {(approval) => (
                      <tr class="cursor-pointer border-b border-v2-border-border-muted last:border-0 hover:bg-v2-overlay-simple-overlay-hover"
                        onClick={(event) => {
                          if ((event.target as HTMLElement).closest("button, a, input, select, textarea")) return
                          openDetails(approval)
                        }}>
                        <td class="px-5 py-4">
                          <button type="button" class="block max-w-[360px] truncate text-left text-[13px] font-medium hover:underline focus-visible:underline"
                            aria-label={language.t("cowork.enterprise.approvals.viewDetails", { report: approval.report_title })}
                            onClick={() => openDetails(approval)}>{approval.report_title}</button>
                          <span class="mt-0.5 block text-[10px] text-v2-text-text-muted">
                            {[approval.project_name, approval.period_name, approval.report_format.toUpperCase()]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </td>
                        <td class="px-4 py-4 text-[12px] text-v2-text-text-muted">{approval.approver_email}</td>
                        <td class="px-4 py-4"><ApprovalStatus status={approval.status} /></td>
                        <td class="px-4 py-4 text-[11px] text-v2-text-text-muted">
                          {language.formatDateTime(Date.parse(approval.created_at))}
                        </td>
                        <td class="px-4 py-4 text-[11px] text-v2-text-text-muted">
                          {language.formatDateTime(Date.parse(approval.updated_at))}
                        </td>
                        <td class="px-5 py-4">
                          <div class="flex justify-end gap-1">
                            <IconButtonV2
                              size="small"
                              variant="ghost"
                              icon={<IconV2 name="outline-square-arrow" size="small" />}
                              aria-label={language.t("cowork.enterprise.approvals.details")}
                              title={language.t("cowork.enterprise.approvals.details")}
                              onClick={() => openDetails(approval)}
                            />
                            <IconButtonV2
                              size="small"
                              variant="ghost"
                              icon={<IconV2 name="edit" size="small" />}
                              aria-label={language.t("cowork.enterprise.approvals.editReviewer")}
                              title={language.t("cowork.enterprise.approvals.editReviewer")}
                              onClick={() => setState({ editing: approval, reviewerEmail: approval.approver_email, error: "" })}
                            />
                            <IconButtonV2
                              size="small"
                              variant="ghost"
                              class="text-v2-state-fg-danger"
                              icon={<IconV2 name="trash" size="small" />}
                              aria-label={language.t("common.delete")}
                              title={language.t("common.delete")}
                              onClick={() => setState({ deleting: approval, error: "" })}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
        </section>
      </div>

      <Show when={state.editing}>
        <Portal>
          <Modal title={language.t("cowork.enterprise.approvals.editReviewer")} onClose={() => setState("editing", undefined)}>
            <form onSubmit={saveReviewer}>
              <label class="grid gap-2 text-[12px] font-medium">
                {language.t("cowork.enterprise.approvals.reviewer")}
                <input
                  ref={(element) => queueMicrotask(() => element.select())}
                  type="email"
                  required
                  class="h-11 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none"
                  value={state.reviewerEmail}
                  onInput={(event) => setState("reviewerEmail", event.currentTarget.value)}
                />
              </label>
              <div class="mt-5 flex justify-end gap-2">
                <button type="button" class="rounded-md border border-v2-border-border-base px-4 py-2 text-[12px]" onClick={() => setState("editing", undefined)}>{language.t("common.cancel")}</button>
                <button type="submit" class="rounded-md bg-v2-background-bg-contrast px-4 py-2 text-[12px] font-semibold text-v2-text-text-contrast" disabled={!!state.saving}>{language.t("common.save")}</button>
              </div>
            </form>
          </Modal>
        </Portal>
      </Show>

      <Show when={state.deleting}>
        {(approval) => (
          <Portal>
            <Modal title={language.t("cowork.enterprise.approvals.deleteTitle")} onClose={() => setState("deleting", undefined)}>
              <p class="text-[12px] leading-5 text-v2-text-text-muted">
                {language.t("cowork.enterprise.approvals.deleteDescription", { report: approval().report_title })}
              </p>
              <div class="mt-5 flex justify-end gap-2">
                <button type="button" class="rounded-md border border-v2-border-border-base px-4 py-2 text-[12px]" onClick={() => setState("deleting", undefined)}>{language.t("common.cancel")}</button>
                <button type="button" class="rounded-md bg-v2-state-fg-danger px-4 py-2 text-[12px] font-semibold text-white" disabled={!!state.saving} onClick={() => void removeApproval()}>{language.t("common.delete")}</button>
              </div>
            </Modal>
          </Portal>
        )}
      </Show>
    </div>
  )
}

function Metric(props: { label: string; value: number }) {
  return <div class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-4"><div class="text-[10px] font-semibold uppercase tracking-[0.12em] text-v2-text-text-muted">{props.label}</div><div class="mt-2 text-[24px] font-semibold tabular-nums">{props.value}</div></div>
}

function ApprovalStatus(props: { status: string }) {
  const language = useLanguage()
  const kind = () => approvalKind(props.status)
  return <span class="rounded-md px-2 py-1 text-[10px] font-medium" classList={{ "bg-v2-state-bg-success text-v2-state-fg-success": kind() === "approved", "bg-v2-state-bg-danger text-v2-state-fg-danger": kind() === "rejected", "bg-v2-state-bg-warning text-v2-state-fg-warning": kind() === "requested" }}>{language.t(`cowork.enterprise.approvals.${kind()}` as keyof typeof import("@/i18n/en").dict)}</span>
}

function approvalKind(status: string): "requested" | "approved" | "rejected" {
  if (status.toLowerCase() === "approved") return "approved"
  if (status.toLowerCase() === "rejected") return "rejected"
  return "requested"
}

function Modal(props: { title: string; children: JSX.Element; onClose: () => void }) {
  return <div class="fixed inset-0 z-[120] flex items-center justify-center bg-v2-overlay-simple-overlay-scrim p-4" onClick={props.onClose}><section class="w-full max-w-md rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-5 shadow-[var(--v2-elevation-raised)]" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}><h2 class="mb-4 text-[16px] font-semibold">{props.title}</h2>{props.children}</section></div>
}
